import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { FormField } from '../../components/FormField'
import { Input } from '../../components/Input'
import { ApiError, registryApi } from '../../lib/api'

function suggestFromTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 30)
}

function rand4() {
  const a = 'abcdefghijkmnpqrstuvwxyz23456789'
  let s = ''
  for (let i = 0; i < 4; i++) s += a[Math.floor(Math.random() * a.length)]
  return s
}

export default function CreateRegistry() {
  const nav = useNavigate()
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [slugDirty, setSlugDirty] = useState(false)
  const [dueDate, setDueDate] = useState('')

  const [check, setCheck] = useState<{ available: boolean; suggestions: string[] } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (slugDirty) return
    if (!title) {
      setSlug('')
      return
    }
    setSlug(`${suggestFromTitle(title)}-${rand4()}`)
  }, [title, slugDirty])

  useEffect(() => {
    if (!slug || slug.length < 3) {
      setCheck(null)
      return
    }
    const id = setTimeout(() => {
      registryApi
        .checkSlug(slug)
        .then(setCheck)
        .catch(() => setCheck(null))
    }, 400)
    return () => clearTimeout(id)
  }, [slug])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setErr(null)
    try {
      const r = await registryApi.create({
        title,
        slug,
        dueDate: dueDate || undefined,
      })
      nav(`/me/r/${r.id}`)
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 409) setErr("Cette adresse vient d'être prise.")
      else setErr('Erreur lors de la création.')
      setSubmitting(false)
    }
  }

  const canSubmit = !!title && !!slug && !submitting && (check ? check.available : true)

  const hintUrl = `liste-naissance.app/r/${slug || '...'}`
  const slugError =
    check && !check.available
      ? `Indisponible — essayez : ${(check.suggestions ?? []).join(', ')}`
      : undefined

  return (
    <main style={{ maxWidth: 520, margin: '0 auto', padding: '2rem 1rem' }}>
      <Card>
        <h1 style={{ fontFamily: 'var(--font-heading)', marginTop: 0 }}>Créons votre liste</h1>
        <form onSubmit={onSubmit}>
          <FormField label="Nom de la liste" required>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              placeholder="Bébé Martin 🍼"
            />
          </FormField>

          <FormField label="Adresse de votre liste" required hint={hintUrl} error={slugError}>
            <Input
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value.toLowerCase())
                setSlugDirty(true)
              }}
              required
              minLength={3}
              maxLength={40}
              pattern="[-a-z0-9]+"
            />
          </FormField>

          <FormField label="Date prévue d'arrivée">
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </FormField>

          {err ? <p style={{ color: '#B23A48' }}>{err}</p> : null}

          <Button type="submit" disabled={!canSubmit}>
            {submitting ? '…' : 'Créer la liste'}
          </Button>
        </form>
      </Card>
    </main>
  )
}
